/**
 * i18n — the Facilitator Portal in English and Bahasa Indonesia.
 *
 * Scoring 1 Oct: the first Indonesian facilitator ran a Bahasa drill in an
 * English-only portal. Same pattern as the learner site (playbook-web
 * src/lib/i18n.tsx), smaller:
 *   - one file of words per language: src/locales/en.json, id.json;
 *   - `t('key', { name })` fills {name} placeholders;
 *   - the choice is remembered in this browser (fp-locale-v1), and the
 *     default follows the browser's language;
 *   - the language goes to the server on every request (X-Playbook-Locale)
 *     and with the sign-in code request, so the email matches.
 *
 * A word not yet written in Indonesian shows in English, never as its key:
 * Content Authoring writes id.json, and screens move over one at a time.
 */
import { createContext, useContext } from 'react';
import en from '../locales/en.json';
import id from '../locales/id.json';

export type Locale = 'en' | 'id';
const BUNDLES: Record<Locale, Record<string, string>> = { en, id };
export const LS_KEY = 'fp-locale-v1';

/** The stored choice, else the browser's language, else English. Usable outside React. */
export function currentLocale(): Locale {
  try {
    const stored = localStorage.getItem(LS_KEY);
    if (stored === 'en' || stored === 'id') return stored;
  } catch { /* storage blocked */ }
  const nav = typeof navigator !== 'undefined' ? navigator.language || '' : '';
  return nav.toLowerCase().startsWith('id') ? 'id' : 'en';
}

export type TFunction = (key: string, params?: Record<string, string | number>) => string;

export function translate(locale: Locale, key: string, params?: Record<string, string | number>): string {
  let text = BUNDLES[locale][key] || BUNDLES.en[key];
  if (text === undefined) {
    if (import.meta.env.DEV) console.warn(`[i18n] missing key: ${key}`);
    text = key;
  }
  if (params) {
    for (const [k, v] of Object.entries(params)) text = text.split(`{${k}}`).join(String(v));
  }
  return text;
}

export interface I18nValue {
  locale: Locale;
  setLocale: (l: Locale) => void;
  t: TFunction;
}

export const I18nContext = createContext<I18nValue | null>(null);

export function useI18n(): I18nValue {
  const ctx = useContext(I18nContext);
  if (ctx) return ctx;
  // Outside the provider (tests, stray renders): English, no switching.
  return { locale: 'en', setLocale: () => {}, t: (key, params) => translate('en', key, params) };
}
